import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';

interface LocationData {
  city: string;
  country: string;
  displayName: string; // e.g., "Berlin, DE"
}

interface LocationContextType {
  selectedLocation: LocationData | null;
  setSelectedLocation: (location: LocationData | null) => void;
  isLocationSet: boolean;
  resetToProfileLocation: () => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

const LOCATION_STORAGE_KEY = 'treffpunkts-selected-location';

// Restore the last selected location synchronously so the dashboard can render
// location-based content immediately, without waiting for the profile fetch.
const readStoredLocation = (): LocationData | null => {
  try {
    const raw = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LocationData>;
    if (typeof parsed?.city === 'string' && parsed.city && typeof parsed?.country === 'string' && parsed.country) {
      return {
        city: parsed.city,
        country: parsed.country,
        displayName: parsed.displayName || `${parsed.city}, ${parsed.country}`,
      };
    }
  } catch {
    // Corrupted value or storage unavailable — fall through to profile-based init
  }
  return null;
};

interface LocationProviderProps {
  children: ReactNode;
}

export const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  const { userProfile } = useAuth();
  const [selectedLocation, setSelectedLocationState] = useState<LocationData | null>(readStoredLocation);

  // Keep the stored location in sync with the current selection
  useEffect(() => {
    try {
      if (selectedLocation) {
        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(selectedLocation));
      } else {
        localStorage.removeItem(LOCATION_STORAGE_KEY);
      }
    } catch {
      // Storage unavailable (private mode/quota) — selection still works in-memory
    }
  }, [selectedLocation]);

  // Initialize location from user profile when available
  useEffect(() => {
    if (userProfile?.city && userProfile?.country && !selectedLocation) {
      const profileLocation: LocationData = {
        city: userProfile.city,
        country: userProfile.country,
        displayName: `${userProfile.city}, ${userProfile.country}`
      };
      setSelectedLocationState(profileLocation);
      console.log('📍 Location initialized from user profile:', profileLocation);
    }
  }, [userProfile, selectedLocation]);

  const setSelectedLocation = (location: LocationData | null) => {
    setSelectedLocationState(location);
    console.log('📍 Location updated:', location);
  };

  const resetToProfileLocation = () => {
    if (userProfile?.city && userProfile?.country) {
      const profileLocation: LocationData = {
        city: userProfile.city,
        country: userProfile.country,
        displayName: `${userProfile.city}, ${userProfile.country}`
      };
      setSelectedLocationState(profileLocation);
      console.log('📍 Location reset to profile:', profileLocation);
    } else {
      setSelectedLocationState(null);
      console.log('📍 Location reset to null (no profile location)');
    }
  };

  const isLocationSet = !!selectedLocation;

  const value: LocationContextType = {
    selectedLocation,
    setSelectedLocation,
    isLocationSet,
    resetToProfileLocation,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}; 